import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { en, type TranslationKeys } from './en';
import { fr } from './fr';

export type SupportedLang = 'en' | 'fr';

const translations: Record<SupportedLang, TranslationKeys> = { en, fr };

interface LangContextValue {
  lang: SupportedLang;
  t: TranslationKeys;
  setLang: (lang: SupportedLang) => void;
}

const LangContext = createContext<LangContextValue>({
  lang: 'en',
  t: en,
  setLang: () => {},
});

const STORAGE_KEY = 'ailys_partner_lang';

function detectLang(): SupportedLang {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'fr') return stored;

  const browserLang = navigator.language?.toLowerCase() ?? '';
  if (browserLang.startsWith('fr')) return 'fr';
  return 'en';
}

export function LangProvider({ initialLang, children }: { initialLang?: SupportedLang; children: ReactNode }) {
  const [lang, setLangState] = useState<SupportedLang>(initialLang ?? detectLang);

  const setLang = useCallback((next: SupportedLang) => {
    setLangState(next);
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value: LangContextValue = {
    lang,
    t: translations[lang],
    setLang,
  };

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

export function fmt(template: string, vars: Record<string, string | number>): string {
  let result = template;
  for (const [key, val] of Object.entries(vars)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(val));
  }
  return result;
}
